// apps/client/src/utils/configResolution.ts
// Single source of truth for point-in-time config resolution.
// Used by: getConfigForMonth (Settings + View pages), findActiveSalary (reconciliation).

interface ConfigPeriod {
  effectiveFrom: Date;
  effectiveTo?: Date | null;
}

/**
 * Find the config that was active at the start of a given month.
 *
 * Resolution order:
 * 1. Most recent config with effectiveFrom <= month start (historically correct)
 * 2. Currently active config (effectiveTo = null) — handles mid-month creation
 * 3. Latest config by effectiveFrom — final fallback
 *
 * The old pattern fell back to the EARLIEST config, which was typically $0 base /
 * default rates from initial setup. This caused silent failures when configs were
 * created mid-month (e.g., Mar 27 config invisible for '2026-03' lookup because
 * effectiveFrom Mar 27 > monthStart Mar 1).
 */
export function findActiveConfig<T extends ConfigPeriod>(
  month: string, // 'YYYY-MM'
  configs: T[]
): T | null {
  if (configs.length === 0) return null;

  // UTC, deliberately. Without the `Z` this parses in the VIEWER'S timezone, so in
  // Brisbane (UTC+10) the boundary for '2026-06' becomes 2026-05-31T14:00:00Z — ten
  // hours BEFORE a config stamped effectiveFrom 2026-06-01T00:00:00Z. Any config
  // effective on the 1st of a month was then missed for that entire month and the
  // resolver fell through to the previous one, shifting every deliberately
  // month-boundary-dated period (salary periods especially) one month late.
  // recipientCommissionView.configForMonth has always parsed as UTC; this makes the
  // client agree with the server rather than the other way round.
  const monthStart = new Date(`${month}-01T00:00:00.000Z`);

  // Sort by effectiveFrom descending (newest first)
  const sorted = [...configs].sort(
    (a, b) => b.effectiveFrom.getTime() - a.effectiveFrom.getTime()
  );

  // 1. Most recent config effective at or before month start
  for (const cfg of sorted) {
    if (cfg.effectiveFrom <= monthStart) {
      return cfg;
    }
  }

  // 2. Currently active config (effectiveTo = null) — covers mid-month creation
  const active = sorted.find(c => !c.effectiveTo);
  if (active) return active;

  // 3. Final fallback: latest config
  return sorted[0];
}
